/**
 * Drop-in replacement for the `xlsx` (SheetJS) library using ExcelJS.
 * Provides the same API surface used across this codebase.
 * ExcelJS is primary; SheetJS (xlsx) is fallback for files ExcelJS cannot parse
 * (e.g. shared formula bugs in certain Excel files).
 */
import ExcelJS from 'exceljs';
import * as SheetJS from 'xlsx';

export interface Worksheet {
  data: any[][];
  name: string;
  colWidths?: number[];
  '!cols'?: { wch: number }[];
  [key: string]: any;
}

export interface Workbook {
  sheets: Worksheet[];
}

// ─── book_new ────────────────────────────────────────────────────────────────
function book_new(): Workbook {
  return { sheets: [] };
}

// ─── json_to_sheet ───────────────────────────────────────────────────────────
function json_to_sheet(data: Record<string, any>[]): Worksheet {
  if (!data || data.length === 0) return { data: [], name: '' };
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => row[h] ?? ''));
  return { data: [headers, ...rows], name: '' };
}

// ─── aoa_to_sheet ────────────────────────────────────────────────────────────
function aoa_to_sheet(data: any[][]): Worksheet {
  return { data, name: '' };
}

// ─── book_append_sheet ───────────────────────────────────────────────────────
function book_append_sheet(wb: Workbook, ws: Worksheet, name: string): void {
  wb.sheets.push({ ...ws, name });
}

// ─── sheet_to_json ───────────────────────────────────────────────────────────
function sheet_to_json(ws: Worksheet, opts?: { header?: number | 'A' | 1 }): any[] {
  const { data } = ws;
  if (!data || data.length === 0) return [];

  if (opts?.header === 1) {
    // Return array of arrays
    return data;
  }

  // Return array of objects using first row as headers
  const headers = data[0] as string[];
  return data.slice(1).map(row => {
    const obj: Record<string, any> = {};
    headers.forEach((h, i) => { obj[h] = row[i]; });
    return obj;
  });
}

// ─── read (parse uploaded Excel file) ────────────────────────────────────────
function read(data: Uint8Array | ArrayBuffer, _opts?: any): Workbook {
  // ExcelJS requires async reading; we return a sync-compatible shim.
  // The actual parsing happens in the async helpers below (readAsync).
  // This stub exists so code that calls XLSX.read() synchronously still compiles.
  // Use xlsxCompat.readAsync() in async contexts.
  throw new Error(
    'xlsxCompat.read() is not supported. Use xlsxCompat.readAsync(arrayBuffer) instead.'
  );
}

// Converts any ExcelJS cell value to a plain primitive (string/number/boolean/null).
// Prevents formula objects, rich-text objects, and shared-formula objects from
// leaking into the data array — ExcelJS writeBuffer() chokes on them.
function sanitizeCellValue(v: any): string | number | boolean | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') {
    // Rich text: { richText: [{ text: '...' }, ...] }
    if (Array.isArray(v.richText)) return v.richText.map((r: any) => r.text || '').join('');
    // Formula / shared formula: { formula/sharedFormula: '...', result: ... }
    if (v.result !== undefined) return typeof v.result === 'object' ? String(v.result ?? '') : (v.result ?? null);
    if (v.formula !== undefined || v.sharedFormula !== undefined) return null;
    // Hyperlink: { text: '...', hyperlink: '...' }
    if (v.text !== undefined) return String(v.text);
    return String(v);
  }
  return String(v);
}

// ─── readAsync (replaces XLSX.read for async callers) ────────────────────────
async function readAsync(arrayBuffer: ArrayBuffer): Promise<Workbook> {
  // Try ExcelJS first
  try {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(arrayBuffer);

    const sheets: Worksheet[] = [];
    wb.eachSheet(sheet => {
      const data: any[][] = [];
      sheet.eachRow(row => {
        // sanitize every cell so no formula objects reach writeAsync
        const sanitized = (row.values as any[]).slice(1).map(sanitizeCellValue);
        data.push(sanitized);
      });
      sheets.push({ name: sheet.name, data });
    });

    return { sheets };
  } catch (excelJsErr: any) {
    // ExcelJS fails on files with shared formulas — fall back to SheetJS (read-only, cellFormula disabled)
    console.warn('[xlsxCompat] ExcelJS failed, falling back to SheetJS:', excelJsErr.message);
    try {
      const uint8 = new Uint8Array(arrayBuffer);
      const wb = SheetJS.read(uint8, { type: 'array', cellFormula: false, sheetStubs: true });

      const sheets: Worksheet[] = wb.SheetNames.map(name => {
        const ws = wb.Sheets[name];
        const data: any[][] = SheetJS.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
        return { name, data };
      });

      return { sheets };
    } catch (sheetJsErr: any) {
      console.error('[xlsxCompat] Both ExcelJS and SheetJS failed:', sheetJsErr.message);
      throw new Error(`Could not parse Excel file: ${excelJsErr.message}`);
    }
  }
}

// ─── write (serialize workbook → Uint8Array) ─────────────────────────────────
async function writeAsync(wb: Workbook, _opts?: any): Promise<Uint8Array> {
  const excelWb = new ExcelJS.Workbook();

  for (const sheet of wb.sheets) {
    const ws = excelWb.addWorksheet(sheet.name || 'Sheet1');

    const colDefs = sheet['!cols'] || (sheet.colWidths ? sheet.colWidths.map(w => ({ wch: w })) : null);
    if (colDefs) {
      ws.columns = colDefs.map((col: { wch: number }, i: number) => ({
        key: String(i),
        width: col.wch
      }));
    }

    for (const row of sheet.data) {
      ws.addRow(row);
    }
  }

  const buffer = await excelWb.xlsx.writeBuffer();
  return new Uint8Array(buffer as ArrayBuffer);
}

// ─── writeFile (download workbook as file) ────────────────────────────────────
async function writeFile(wb: Workbook, filename: string, _opts?: any): Promise<void> {
  const buffer = await writeAsync(wb);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── write (sync-like, returns array — wraps async internally) ────────────────
// Note: this is async under the hood; callers must await xlsxCompat.write()
async function write(wb: Workbook, _opts?: any): Promise<Uint8Array> {
  return writeAsync(wb);
}

export const utils = { book_new, json_to_sheet, aoa_to_sheet, book_append_sheet, sheet_to_json };
export { book_new, json_to_sheet, aoa_to_sheet, book_append_sheet, sheet_to_json, read, readAsync, write, writeAsync, writeFile };
